import {
  Menu,
  useMediaQuery,
  useTheme,
  makeStyles,
  Box,
} from '@material-ui/core'
import React, { useMemo } from 'react'
import {
  Datagrid,
  Filter,
  FunctionField,
  ListProps,
  Pagination,
  ReferenceField,
  TextField,
  TextInput,
  sanitizeFetchType,
  useListContext,
  useQuery,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { ResourceName } from '../../../core/ResourceName'
import Button from '../../common/customized-mui-components/Button'
import List from '../../common/customized-ra-components/List'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import FilterProps from '../../common/FilterProps'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import {
  CheckIfAnyModuleHasError,
  CheckLastRefreshError,
  DeviceStatusesPreview,
} from '../common/modules/ModuleStatus'
import {
  TurnstileDto,
  TurnstileStatusData,
} from '../../../core/dto/device/common/modules/turnstile.dto'
import { getModulesFromJsonStatus } from '../../../core/devices/common/modules/module.mapper'
import { DeviceCategoryExtendedDto } from '../../../core/dto/device-category/device-category-extended.dto'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { BarrierSetPPOZCompactButton } from './barrier-list/BarrierSetPPOZCompactButton'
import { BarrierSetPowerCompactButton } from './barrier-list/BarrierSetPowerCompactButton'
import { BarrierSetPPOZBulkButton } from './barrier-list/BarrierSetPPOZBulkButton'
import { BarrierSetPowerBulkButton } from './barrier-list/BarrierSetPowerBulkButton'
import { BarrierSetFreePassBulkButton } from './barrier-list/BarrierSetFreePassBulkButton'
import { BarrierSetFreePassCompactButton } from './barrier-list/BarrierSetFreePassCompactButton'
import { DeviceResetModulesBulkButton } from '../../common/DeviceResetModulesBulkButton'
import { useColorPalette } from '../../../core/devices/devices.utils'
import { DeviceStatusCheck } from '../../../core/enum/DeviceStatusCheck'

const useStyles = makeStyles((theme) => ({
  compactStatus: {
    display: 'flex',
    gap: theme.spacing(0.5),
    height: 34,
    cursor: 'default',
  },
  iconContainer: {
    position: 'relative',
    width: 34,
  },
  icon: {
    position: 'absolute',
    left: 5,
    top: 5,
  },
  crossIcon: {
    position: 'absolute',
    left: 0,
    top: 0,
    fontSize: 34,
    opacity: 0.8,
  },
}))

const BarrierFilters = (props: FilterProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()

  return (
    <Filter {...props}>
      <TextInput source="name" alwaysOn autoFocus />
      {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
        <FilteredReferenceInput
          label="resources.barriers.fields.categoryId"
          source="categoryIdWithDescendants"
          reference={ResourceName.DEVICE_CATEGORIES}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'hierarchyString',
            showFilter: true,
          }}
        />
      )}
      {hasAuthority(Authority.VIEW_TS_CANS) && (
        <FilteredReferenceInput
          label="resources.barriers.fields.tsCanId"
          source="tsCanId"
          reference={ResourceName.TS_CANS}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            showFilter: true,
          }}
        />
      )}
    </Filter>
  )
}

const BarrierGridExpand = ({ ...props }) => {
  const { record, serverDateTime } = props
  return (
    <DeviceStatusesPreview
      jsonStatus={record.jsonStatus}
      deviceType={DeviceTypes.BARRIERS}
      referenceDateTime={serverDateTime}
    />
  )
}

const BarriersBulkActions = ({ ...props }) => {
  const [anchorEl, setAnchorEl] = React.useState(null)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const BarrierSetFreePassAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <BarrierSetFreePassBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const BarrierSetFreePassRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <BarrierSetFreePassBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const BarrierSetPowerAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <BarrierSetPowerBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const BarrierSetPowerRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <BarrierSetPowerBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const BarrierSetPPOZAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <BarrierSetPPOZBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const BarrierSetPPOZRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <BarrierSetPPOZBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          asRecurringJob
        />
      )),
    [props.selectedIds],
  )

  const BarrierResetModulesAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DeviceResetModulesBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          {...forwardRefProps}
          resource={ResourceName.BARRIERS}
        />
      )),
    [props.selectedIds],
  )

  return (
    <div>
      <Button
        onClick={handleClick}
        label="const.selectAction"
        variant="text"
        color="default"
        useSmallVersionBreakpoint={false}
      />
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={anchorEl != null}
        onClose={handleClose}
        onClick={handleClose}
      >
        <BarrierSetFreePassAction />
        <BarrierSetFreePassRecurringAction />
        <BarrierSetPowerAction />
        <BarrierSetPowerRecurringAction />
        <BarrierSetPPOZAction />
        <BarrierSetPPOZRecurringAction />
        <BarrierResetModulesAction />
      </Menu>
    </div>
  )
}

const CompactStatus = ({ ...props }) => {
  const { record } = props
  const { jsonStatus: rawJsonStatus, statusIndex } = record
  const modules = getModulesFromJsonStatus(rawJsonStatus)
  const turnstileStatusDataLength = (modules?.[0] as TurnstileDto)
    ?.turnstileStatusData?.length
  let convertedStatusIndex = 0
  if (
    turnstileStatusDataLength &&
    turnstileStatusDataLength > 0 &&
    statusIndex !== undefined &&
    statusIndex < turnstileStatusDataLength
  ) {
    convertedStatusIndex = statusIndex
  }
  const status =
    turnstileStatusDataLength === undefined
      ? (modules?.[0] as TurnstileStatusData)
      : (modules?.[0] as TurnstileDto)?.turnstileStatusData?.[
          convertedStatusIndex
        ]
  const classes = useStyles()

  if (status) {
    return (
      <Box
        className={classes.compactStatus}
        onClick={(event) => event.stopPropagation()}
      >
        <BarrierSetPowerCompactButton PowerTurnedOn={status?.powerTurnedOn} />
        <BarrierSetPPOZCompactButton FireAlarm={status?.fireAlarm} />
        <BarrierSetFreePassCompactButton
          FreePassForward={status?.freePassForward}
        />
      </Box>
    )
  }
  return <div />
}

export const BarriersList = ({ ...props }: ListProps) => {
  const { loading } = useListContext()
  const theme = useTheme()
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const hasAuthority = useHasAuthority()
  const colorPalette = useColorPalette(theme)

  const { data: serverDate } = useQuery({
    type: sanitizeFetchType('getServerDateTime'),
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })

  const postRowStyle = (record: any) => {
    if (!serverDate) return {}

    const barrierError = CheckLastRefreshError(
      record?.lastRefresh,
      serverDate?.serverDateTime,
    )
    if (barrierError) return colorPalette(DeviceStatusCheck.ERROR)

    const barrierWarning = CheckIfAnyModuleHasError(
      record.jsonStatus,
      DeviceTypes.BARRIERS,
      serverDate?.serverDateTime,
    )

    if (barrierWarning) return colorPalette(DeviceStatusCheck.WARNING)
    return colorPalette(DeviceStatusCheck.SUCCESS)
  }

  return (
    <List
      {...props}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      exporter={false}
      filters={<BarrierFilters />}
      sort={{
        field: 'id',
        order: 'DESC',
      }}
      bulkActionButtons={<BarriersBulkActions />}
    >
      <Datagrid
        rowStyle={postRowStyle}
        size="small"
        loaded={!loading}
        rowClick={(id) => `${ResourceName.BARRIERS}/${id}/show`}
        isRowExpandable={(record) =>
          record?.jsonStatus !== undefined && record?.jsonStatus !== null
        }
        expand={
          <BarrierGridExpand serverDateTime={serverDate?.serverDateTime} />
        }
      >
        <TextField source="id" label="ID" />
        <TextField source="name" />
        {!xSmallScreen && hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.DEVICE_CATEGORIES}
            link={false}
            sortBy="name"
          >
            <FunctionField<DeviceCategoryExtendedDto>
              source="hierarchyString"
              render={(record?: DeviceCategoryExtendedDto) =>
                `${record?.id}. ${record?.hierarchyString}`
              }
            />
          </ReferenceField>
        )}
        <CompactStatus source="jsonStatus" sortable={false} />
      </Datagrid>
    </List>
  )
}
